import React, { useContext, useState, useEffect, useRef } from 'react';
import { FileText, Loader2, Trash2, PlusCircle, X, Package, Search, ChevronDown } from 'lucide-react';

import styles from './styles.module.css';
import { Context } from '../../context/UserContext';
import Modal from '../Modal';
import CardNotes from '../CardNotes';
import UpgradeModal from '../ModalUpgrade';
import useFlashMessage from '../../hooks/useFlashMessage';

export default function AddInvoice() {
    const {
        user, setUser, fetchUser,
        createInvoice, setInvoices,
        getMyProducts, products, setProducts,
    } = useContext(Context);

    const [openUpgradeModal, setOpenUpgradeModal] = useState(false);
    const [currentPlan, setCurrentPlan] = useState('free');
    const { setFlashMessage } = useFlashMessage();
    const [loading, setLoading] = useState(false);
    const [submittingAs, setSubmittingAs] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [createdInvoice, setCreatedInvoice] = useState(null);

    // picker de produtos do estoque
    const [showPicker, setShowPicker]     = useState(null);
    const [pickerSearch, setPickerSearch] = useState('');
    const [pickerProducts, setPickerProducts] = useState([]);
    const pickerRef = useRef();

    const [recipient, setRecipient] = useState({ name: '', cpfCnpj: '', address: '', phone: '' });
    const [services, setServices]   = useState([{ description: '', amount: '', valueUnit: '', fromStock: false, productId: null }]);
    const [taxes, setTaxes]         = useState({ iss: '', irrf: '', csll: '', cofins: '', pis: '' });
    const [obs, setObs]             = useState('');

    const token = JSON.parse(localStorage.getItem('token'));

    /* ── Carrega produtos do estoque ── */
    useEffect(() => {
        (async () => {
            // getMyProducts retorna um array (com .limit e .plan como props extras)
            const data = await getMyProducts(token);
            setProducts(Array.from(data)); // cópia limpa para o state
            setPickerProducts(Array.from(data));
        })();
        // eslint-disable-next-line
    }, []);

    /* ── Filtra picker pelo texto digitado ── */
    useEffect(() => {
        if (!pickerSearch.trim()) {
            setPickerProducts(products);
            return;
        }
        const q = pickerSearch.toLowerCase();
        setPickerProducts(
            products.filter(p =>
                p.name.toLowerCase().includes(q) ||
                (p.category && p.category.toLowerCase().includes(q))
            )
        );
    }, [pickerSearch, products]);

    /* ── Fecha picker ao clicar fora ── */
    useEffect(() => {
        function handleClickOutside(e) {
            if (pickerRef.current && !pickerRef.current.contains(e.target)) {
                setShowPicker(null);
                setPickerSearch('');
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    /* ── Serviços ── */
    const addService = () => {
        setServices(prev => [...prev, { description: '', amount: '', valueUnit: '', fromStock: false, productId: null }]);
    };

    const removeService = (index) => {
        setServices(prev => prev.filter((_, i) => i !== index));
    };

    const updateService = (index, field, value) => {
        setServices(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            // Se edita a descrição manualmente, desvincula do estoque
            if (field === 'description' && updated[index].fromStock) {
                updated[index].fromStock = false;
                updated[index].productId = null;
            }
            return updated;
        });
    };

    const selectProduct = (index, product) => {
        setServices(prev => {
            const updated = [...prev];
            updated[index] = {
                description: product.name,
                amount:      updated[index].amount || 1,
                valueUnit:   product.price,
                productId:   product._id,
                fromStock:   true,
            };
            return updated;
        });
        setShowPicker(null);
        setPickerSearch('');
    };

    const removeFromStock = (index) => {
        setServices(prev => {
            const updated = [...prev];
            updated[index] = { description: '', amount: '', valueUnit: '', productId: null, fromStock: false };
            return updated;
        });
    };

    /* ── Submit ── */
    const handleSubmit = async (e, isReceipt = false) => {
        e.preventDefault();
        setLoading(true);
        setSubmittingAs(isReceipt ? 'receipt' : 'invoice');

        const servicesFormatted = services.map(s => ({
            description: s.description,
            amount:      Number(s.amount),
            valueUnit:   Number(s.valueUnit),
            productId:   s.productId || null,
            fromStock:   s.fromStock || false,
        }));

        const taxesFormatted = {
            iss:    Number(taxes.iss),
            irrf:   Number(taxes.irrf),
            csll:   Number(taxes.csll),
            cofins: Number(taxes.cofins),
            pis:    Number(taxes.pis),
        };
        console.log("invoiceData (user.localization): ", user.localization)
        //Ex: R. nome-da-rua, número - nome-do-bairro, cidade estado.
        const userAddress = `${user.localization.address}, ${user.localization.city} ${user.localization.state};`
        const invoiceData = {
            provider: {
                name:    user.name    || 'Prestador Exemplo',
                cnpj:    user.cnpj    || '00.000.000/0000-00',
                address: userAddress || 'Endereço não informado',
            },
            recipient,
            services: servicesFormatted,
            taxes:    taxesFormatted,
            obs,
            isReceipt,
        };

        const response = await createInvoice(token, invoiceData);

        if (response.upgradeRequired) {
            setCurrentPlan(response.currentPlan || 'free');
            setOpenUpgradeModal(true);
            setLoading(false);
            setSubmittingAs(null);
            return;
        }

        if (response.error) {
            setLoading(false);
            setSubmittingAs(null);
            return;
        }
        console.log("const response = await createInvoice(token, invoiceData); ", response.invoice)

        setInvoices(prev => [response.invoice, ...prev]);
        setCreatedInvoice(response.invoice);
        setShowModal(true);
        await fetchUser();
        setLoading(false);
        setSubmittingAs(null);
    };

    /* ── Render ── */
    return (
        <div className={styles.container}>

            {openUpgradeModal && (
                <UpgradeModal
                    currentPlan={currentPlan}
                    setOpenUpgradeModal={setOpenUpgradeModal}
                />
            )}
            
            {!showModal && (
                <form onSubmit={handleSubmit} className={styles.form}>

                    {/* ── Tomador ── */}
                    <fieldset className={styles.fieldset}>
                        <legend>Dados do Tomador (Cliente)</legend>
                        <input type="text" placeholder="Nome do Cliente" value={recipient.name}
                            onChange={e => setRecipient({ ...recipient, name: e.target.value })} />
                        <input type="text" placeholder="CPF/CNPJ" value={recipient.cpfCnpj}
                            onChange={e => setRecipient({ ...recipient, cpfCnpj: e.target.value })} />
                        <input type="text" placeholder="Endereço" value={recipient.address}
                            onChange={e => setRecipient({ ...recipient, address: e.target.value })} />
                        <input type="text" placeholder="Telefone (opcional)" value={recipient.phone}
                            onChange={e => setRecipient({ ...recipient, phone: e.target.value })} />
                    </fieldset>

                    {/* ── Serviços / Produtos ── */}
                    <fieldset className={styles.fieldset}>
                        <legend>Serviços / Produtos</legend>

                        {services.map((service, index) => (
                            <div key={index} className={styles.serviceBlock}>
                                <div className={styles.serviceDescRow}>
                                    <div className={styles.descInputWrap}>
                                        <input
                                            type="text"
                                            placeholder="Descrição do serviço"
                                            value={service.description}
                                            onChange={e => updateService(index, 'description', e.target.value)}
                                            required
                                        />
                                        {service.fromStock && (
                                            <>
                                                <small className={styles.smallItem}>Produto vinculado ao estoque</small>
                                                <button type="button" className={styles.removeStockBtn}
                                                    onClick={() => removeFromStock(index)} title="Remover vínculo">
                                                    <X size={14} />
                                                </button>
                                            </>
                                        )}
                                        <button
                                            type="button"
                                            className={styles.pickerToggle}
                                            onClick={() => {
                                                setShowPicker(showPicker === index ? null : index);
                                                setPickerSearch('');
                                            }}
                                            title="Selecionar do estoque"
                                        >
                                            <Package size={16} />
                                            <ChevronDown size={14} />
                                        </button>
                                    </div>

                                    {showPicker === index && (
                                        <div className={styles.pickerDropdown} ref={pickerRef}>
                                            <div className={styles.pickerSearchWrap}>
                                                <Search size={14} className={styles.pickerSearchIcon} />
                                                <input
                                                    type="text"
                                                    placeholder="Buscar no estoque..."
                                                    value={pickerSearch}
                                                    onChange={e => setPickerSearch(e.target.value)}
                                                    className={styles.pickerSearchInput}
                                                    autoFocus
                                                />
                                            </div>
                                            <ul className={styles.pickerList}>
                                                {pickerProducts.length === 0 ? (
                                                    <li className={styles.pickerEmpty}>Nenhum produto encontrado</li>
                                                ) : (
                                                    pickerProducts.map(product => (
                                                        <li key={product._id} className={styles.pickerItem}
                                                            onClick={() => selectProduct(index, product)}>
                                                            <div className={styles.pickerItemInfo}>
                                                                {product.image ? (
                                                                    <img src={product.image} alt={product.name}
                                                                        className={styles.pickerItemImage} />
                                                                ) : (
                                                                    <div className={styles.pickerItemImagePlaceholder}>
                                                                        <Package size={14} />
                                                                    </div>
                                                                )}
                                                                <span className={styles.pickerItemName}>{product.name}</span>
                                                            </div>
                                                            <div>
                                                                <span className={styles.pickerItemPrice}>
                                                                    Quant. {Number(product.stock).toFixed(0)}
                                                                </span>
                                                                <span className={styles.pickerItemPrice}>
                                                                    R$ {Number(product.price).toFixed(2)}
                                                                </span>
                                                            </div>
                                                        </li>
                                                    ))
                                                )}
                                            </ul>
                                        </div>
                                    )}
                                </div>

                                <div className={styles.serviceRow}>
                                    <input type="number" placeholder="Quantidade" min="1"
                                        value={service.amount}
                                        onChange={e => updateService(index, 'amount', e.target.value)} required />
                                    <div className={styles.priceInput}>
                                        <span>R$</span>
                                        <input type="number" placeholder="Valor unitário" min="0" step="0.01"
                                            value={service.valueUnit}
                                            onChange={e => updateService(index, 'valueUnit', e.target.value)} required />
                                    </div>
                                    {services.length > 1 && (
                                        <button type="button" className={styles.removeBtn} onClick={() => removeService(index)}>
                                            <Trash2 size={18} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}

                        <button type="button" className={styles.addBtn} onClick={addService}>
                            <PlusCircle size={18} /> Adicionar Serviço
                        </button>
                    </fieldset>

                    {/* ── Impostos ── */}
                    <fieldset className={styles.fieldset}>
                        <legend>Impostos (%)</legend>
                        {Object.keys(taxes).map(tax => (
                            <input key={tax} type="number" min="0" step="0.01"
                                placeholder={tax.toUpperCase()} value={taxes[tax]}
                                onChange={e => setTaxes({ ...taxes, [tax]: e.target.value })} />
                        ))}
                    </fieldset>

                    <textarea placeholder="Observações" value={obs}
                        onChange={e => setObs(e.target.value)} className={styles.textarea} />

                    <div className={styles.submitRow}>
                        <button type="submit" className={styles.btn} disabled={loading} onClick={(e) => handleSubmit(e, false)}>
                            {submittingAs === 'invoice' ? <Loader2 className={styles.spinner} /> : 'Gerar Orçamento'}
                        </button>
                        <button type="button" className={styles.btnReceipt} disabled={loading} onClick={(e) => handleSubmit(e, true)}>
                            {submittingAs === 'receipt' ? <Loader2 className={styles.spinner} /> : 'Gerar Recibo'}
                        </button>
                    </div>
                </form>
            )}

            {showModal && (
                <Modal
                    createdInvoice={createdInvoice}
                    setShowModal={setShowModal}
                    setCreatedInvoice={setCreatedInvoice}
                    user={user}
                    setUser={setUser}
                    setFlashMessage={setFlashMessage}
                >
                    <CardNotes createdInvoice={createdInvoice} />
                </Modal>
            )}
        </div>
    );
}